import { useAnnouncer, useToastStore, type Toast } from '../state/toastStore';
import { cx } from './cx';
import { Icon } from './Icon';
import styles from './Toaster.module.css';

/** Toast stack above the tab bar, plus the app's polite/assertive live regions (§5.6, §10). */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  const polite = toasts.filter((t) => t.kind !== 'danger');
  const urgent = toasts.filter((t) => t.kind === 'danger');
  return (
    <div className={styles.stack}>
      <div role="alert" className={styles.group}>
        {urgent.map((t) => (
          <ToastView key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
      <div role="status" aria-live="polite" className={styles.group}>
        {polite.map((t) => (
          <ToastView key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </div>
  );
}

function ToastView({ toast, onDismiss }: { toast: Toast; onDismiss(): void }) {
  const icon = toast.kind === 'success' ? 'check' : toast.kind === 'danger' ? 'alert' : 'info';
  return (
    <div className={cx(styles.toast, styles[toast.kind])}>
      <span className={styles.icon}>
        <Icon name={icon} size={20} />
      </span>
      <p className={styles.message}>{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            toast.action?.run();
            onDismiss();
          }}
        >
          {toast.action.label}
        </button>
      )}
      <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">
        <Icon name="close" size={18} />
      </button>
    </div>
  );
}

export function LiveRegions() {
  const { polite, assertive } = useAnnouncer();
  return (
    <>
      <div className="wb-visually-hidden" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="wb-visually-hidden" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
    </>
  );
}
